import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { ensurePortalRoot, type Placement } from '../shared/index.js';
import { useFloating } from '../../hooks/useFloating.js';
import { useOutsideClick } from '../../hooks/useOutsideClick.js';
import { useRestoreFocus } from '../../hooks/useRestoreFocus.js';
import { useEscapeStack } from '../../hooks/useEscapeStack.js';

export type PopoverTrigger = 'click' | 'hover' | 'manual';

export interface PopoverProps {
  children: ReactElement<HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> }>;
  /** Pop content. Passed a close() callback so menu items can dismiss. */
  content: ReactNode | ((ctx: { close: () => void }) => ReactNode);
  placement?: Placement;
  trigger?: PopoverTrigger;
  /** Controlled open state. Omit for uncontrolled. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  arrow?: boolean;
  /** When true, the panel renders edge-to-edge with no internal padding —
   *  used for panels that own their own layout (NotificationCenter,
   *  DateTimePicker, embedded Menu). Default false. */
  padless?: boolean;
  /** When true, clicks outside the anchor + panel close it. Default true. */
  closeOnOutsideClick?: boolean;
  /** When true, Escape closes. Default true. */
  closeOnEscape?: boolean;
  /** Focus first focusable element in panel on open. Default true for click trigger. */
  autoFocus?: boolean;
  className?: string;
  /** aria-label for the panel itself if content is not self-describing. */
  'aria-label'?: string;
  id?: string;
}

/** Click- or hover-triggered floating panel. Unlike Tooltip, Popover content
 *  is interactive — focus moves into it on open, and dismissal is explicit
 *  (outside click, Esc, or `close()` passed into content fn).
 *
 *  Positioning delegated to `useFloating`; outside-click to `useOutsideClick`;
 *  focus restoration on close to `useRestoreFocus`. */
export function Popover(props: PopoverProps): ReactNode {
  const {
    children,
    content,
    placement = 'bottom',
    trigger = 'click',
    open: controlledOpen,
    defaultOpen = false,
    onOpenChange,
    arrow = false,
    padless = false,
    closeOnOutsideClick = true,
    closeOnEscape = true,
    autoFocus,
    className,
    'aria-label': ariaLabel,
    id,
  } = props;

  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const anchorRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const reactId = useId();
  const panelId = id ?? `${reactId}-popover`;

  const {
    setAnchor,
    setFloating,
    rect,
    floatingStyle,
    arrowStyle,
    maxHeight,
    maxWidth,
  } = useFloating({
    open,
    placement,
  });

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  useRestoreFocus(open);

  useOutsideClick(
    panelRef,
    useCallback(() => {
      close();
    }, [close]),
    { enabled: open && closeOnOutsideClick, extraRefs: [anchorRef] },
  );

  // Escape via the shared modal stack — only the topmost layer responds,
  // so a Popover inside a Dialog closes just the Popover on the first
  // press, leaving the Dialog open until a second press.
  useEscapeStack(
    useCallback(() => {
      close();
      anchorRef.current?.focus();
    }, [close]),
    open && closeOnEscape,
  );

  const shouldAutoFocus = autoFocus ?? trigger === 'click';
  useEffect(() => {
    if (!open || !shouldAutoFocus) return;
    const panel = panelRef.current;
    if (panel === null) return;
    const first = panel.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    (first ?? panel).focus();
  }, [open, shouldAutoFocus]);

  const child = Children.only(children);
  if (!isValidElement(child)) return child;

  const prevRef = (child as ReactElement<{ ref?: Ref<HTMLElement> }>).props.ref;
  const mergedAnchorRef = (node: HTMLElement | null): void => {
    anchorRef.current = node;
    setAnchor(node);
    if (typeof prevRef === 'function') prevRef(node);
    else if (prevRef !== undefined && prevRef !== null) {
      (prevRef as { current: HTMLElement | null }).current = node;
    }
  };

  const mergedPanelRef = (node: HTMLDivElement | null): void => {
    panelRef.current = node;
    setFloating(node);
  };

  const existing = child.props;
  const triggerProps: HTMLAttributes<HTMLElement> & { ref: Ref<HTMLElement> } = {
    ref: mergedAnchorRef,
    'aria-haspopup': 'dialog',
    'aria-expanded': open,
    'aria-controls': open ? panelId : undefined,
  };

  if (trigger === 'click') {
    triggerProps.onClick = (e) => {
      existing.onClick?.(e);
      setOpen(!open);
    };
  } else if (trigger === 'hover') {
    triggerProps.onMouseEnter = (e) => {
      existing.onMouseEnter?.(e);
      setOpen(true);
    };
    triggerProps.onMouseLeave = (e) => {
      existing.onMouseLeave?.(e);
      setOpen(false);
    };
    triggerProps.onFocus = (e) => {
      existing.onFocus?.(e);
      setOpen(true);
    };
    triggerProps.onBlur = (e) => {
      existing.onBlur?.(e);
      setOpen(false);
    };
  }

  const portalRoot = ensurePortalRoot();
  const resolvedContent = typeof content === 'function' ? content({ close }) : content;
  const panelClasses = ['vq-popover', className].filter(Boolean).join(' ');

  return (
    <>
      {cloneElement(child, triggerProps)}
      {open && portalRoot !== null
        ? createPortal(
            <div
              ref={mergedPanelRef}
              id={panelId}
              role="dialog"
              className={panelClasses}
              data-component="Popover"
              data-side={rect?.side ?? 'bottom'}
              data-padless={padless ? 'true' : undefined}
              aria-label={ariaLabel}
              style={{
                ...floatingStyle,
                ...(maxHeight > 0 ? { maxHeight: `${String(maxHeight)}px` } : {}),
                ...(maxWidth > 0 ? { maxWidth: `${String(maxWidth)}px` } : {}),
              }}
              tabIndex={-1}
            >
              {resolvedContent}
              {arrow ? (
                <span
                  className="vq-popover__arrow"
                  aria-hidden="true"
                  style={arrowStyle}
                />
              ) : null}
            </div>,
            portalRoot,
          )
        : null}
    </>
  );
}
